import React, { useCallback, useState } from "react";
import Player from "./index";
import Pairing from "./Pairing";
import {
  loadState, saveState,
  loadPairedDeviceCode, savePairedDeviceCode, clearPairedDeviceCode,
} from "./storage";
import { IPlayerPairedDevice } from "./types";

/**
 * Entry point for the bare `/player` route — the self-service signage
 * player with no device code in the URL.
 *
 *  - First run (no stored credential): shows the {@link Pairing} screen,
 *    which displays a code the admin claims in VM Display. On success the
 *    device credential is persisted under its own device_code (the same
 *    per-device localStorage/IndexedDB layout the `/player/:deviceCode`
 *    flow uses) and a pointer is written so future boots skip pairing.
 *  - Subsequent runs: loads the stored device_code + token and hands
 *    straight to {@link Player}, so a kiosk that reboots comes back
 *    playing with zero interaction.
 *  - Revocation: if the credential turns out to be dead, Player calls
 *    `onCredentialInvalid`; we drop the pointer and fall back to pairing.
 *
 * The `/player/:deviceCode` route is untouched and keeps working for
 * admin-provisioned screens (and Android parity).
 */
const PlayerEntry: React.FC = () => {
  const [pairedCode, setPairedCode] = useState<string | null>(() => {
    const code = loadPairedDeviceCode();
    // A pointer with no stored token is useless — treat as unpaired.
    if (code && loadState(code).token) return code;
    return null;
  });

  const handlePaired = useCallback((device: IPlayerPairedDevice, token: string) => {
    const code = device.device_code;
    // Seed the per-device state so Player's boot finds the token and
    // skips the (unnecessary) /device/register round-trip.
    saveState({
      device_code: code,
      token,
      playlist_version: 0,
      last_sync: null,
      cached_playlist: null,
      last_command: null,
    });
    savePairedDeviceCode(code);
    setPairedCode(code);
  }, []);

  const handleCredentialInvalid = useCallback(() => {
    clearPairedDeviceCode();
    setPairedCode(null);
  }, []);

  if (!pairedCode) {
    return <Pairing onPaired={handlePaired} />;
  }

  return (
    <Player
      key={pairedCode}
      deviceCodeOverride={pairedCode}
      onCredentialInvalid={handleCredentialInvalid}
    />
  );
};

export default PlayerEntry;
